import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { IEntity } from "@/lib/utility/data/entity.interface";
import costService from "@/services/mrfiktiv/services/costService";
import { MrfiktivCostViewModelGen, MrfiktivUpdateCostDtoGen } from "@/services/mrfiktiv/v1/data-contracts";
import { CostDataAccessLayer } from "@/store/modules/access-layers/cost.access-layer";
import { CostGroupDataAccessLayer } from "@/store/modules/access-layers/cost-group.access-layer";
import { IReference, Reference } from "./reference.entity";
import { ITimestamp, Timestamp } from "./timestamp.entity";

@IsFilterable
class CostBase implements IEntity<MrfiktivCostViewModelGen, MrfiktivUpdateCostDtoGen> {
  /** Id of the cost object */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.cost.id"
  })
  id: string;

  /** The partnerId of the cost */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.cost.partnerId"
  })
  partnerId: string;

  /** The total value of the cost object */
  @FilterConfig({
    type: FilterTypes.NUMBER,
    displayName: "objects.cost.total"
  })
  total: number;

  /** Title of the cost object */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.cost.title"
  })
  title: string;

  /** Internal description of the cost object */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.cost.description"
  })
  description: string;

  /** tags */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.cost.tags"
  })
  tags: string[];

  /** file ids */
  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.cost.files"
  })
  files: string[];

  /** refs */
  @FilterConfig({
    type: Reference,
    displayName: "objects.cost.refs"
  })
  refs: IReference[];

  /** cost group */
  @FilterConfig({
    type: FilterTypes.ENUM,
    config: {
      itemCallback: () => CostGroupDataAccessLayer.entities,
      mapItemToComponent: (item: any) => {
        return { item };
      },
      itemValue: "id",
      component: "refs-cost-group"
    },
    displayName: "objects.cost.group"
  })
  group?: string;

  /** timestamp */
  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  constructor(data: Partial<MrfiktivCostViewModelGen>) {
    this.id = data.id ?? "";
    this.partnerId = data.partnerId ?? "";
    this.total = data.total ?? 0;
    this.title = data.title ?? "";
    this.description = data.description ?? "";
    this.tags = data.tags ?? [];
    this.files = data.files ?? [];
    this.refs = (data.refs ?? []).map(ref => new Reference(ref));
    this.group = data.group;
    this.timestamp = data.timestamp ?? { created: "", lastModified: "" };
  }

  map(data: MrfiktivCostViewModelGen): void {
    this.id = data.id;
    this.partnerId = data.partnerId;
    this.total = data.total;
    this.title = data.title;
    this.description = data.description;
    this.tags = data.tags;
    this.files = data.files;
    this.refs = (data.refs ?? []).map(ref => new Reference(ref));
    this.group = data.group;
    this.timestamp = data.timestamp;
  }

  async create(): Promise<this> {
    const res = await costService.create(this.partnerId, {
      title: this.title,
      description: this.description,
      tags: this.tags,
      files: this.files,
      refs: this.refs,
      total: this.total,
      group: this.group
    });

    this.map(res);
    CostDataAccessLayer.set(this);

    return this;
  }

  async fetch(): Promise<this> {
    const res = await costService.getOne(this.partnerId, this.id);
    this.map(res);
    CostDataAccessLayer.set(this);

    return this;
  }

  async updatePartial(dto: Partial<MrfiktivUpdateCostDtoGen>): Promise<this> {
    const res = await costService.update(this.partnerId, this.id, dto);

    this.map(res);
    CostDataAccessLayer.set(this);

    return this;
  }

  async update(): Promise<this> {
    return this.updatePartial({
      title: this.title,
      description: this.description,
      tags: this.tags,
      files: this.files,
      refs: this.refs,
      total: this.total,
      group: this.group
    });
  }

  async delete(): Promise<void> {
    const res = await costService.delete(this.partnerId, this.id);

    this.map(res);
    CostDataAccessLayer.delete(this);
  }
}

type ICost = CostBase;
const Cost = Filter.createForClass(CostBase);

export { Cost, ICost };
